import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ROUTES } from '../utils/constants';

const ManualPaymentRequestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubscription, setIsSubscription] = useState(false);
  const [isBundle, setIsBundle] = useState(false);
  const [bundleItems, setBundleItems] = useState([]);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    ccpSenderName: '',
    receiptFile: null
  });

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
      if (type === 'success') {
        navigate(ROUTES.DASHBOARD);
      }
    }, 3000);
  };

  const checkForConflicts = async (itemIds) => {
    try {
      // Get user's paid purchases
      const purchases = await api.getUserPurchases();
      const paidItemIds = purchases
        .filter(p => p.payment_status === 'paid')
        .map(p => p.course_id || p.id);
      
      // Get user's pending manual payment requests
      const pendingPayments = await api.getUserManualPayments();
      const pendingItemIds = [];
      
      pendingPayments.forEach(payment => {
        if (payment.status === 'pending') {
          if (payment.purchase_type === 'bundle' && payment.item_ids) {
            try {
              const ids = JSON.parse(payment.item_ids);
              pendingItemIds.push(...ids);
            } catch (e) {
              console.error('Error parsing item_ids:', e);
            }
          } else if (payment.course_id) {
            pendingItemIds.push(payment.course_id);
          }
        }
      });
      
      // Check for conflicts
      const alreadyOwned = itemIds.filter(id => paidItemIds.includes(id));
      const pendingConflicts = itemIds.filter(id => pendingItemIds.includes(id));
      
      if (alreadyOwned.length > 0 || pendingConflicts.length > 0) {
        return {
          hasConflict: true,
          alreadyOwned,
          pendingConflicts
        };
      }
      
      return { hasConflict: false };
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { hasConflict: false };
    }
  };

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      return;
    }
    
    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }
    // Check if this is subscription payment
    if (id === 'subscription') {
      setIsSubscription(true);
      setCourse({ 
        title_ar: 'اشتراك شهري', 
        price: 4900,
        description_ar: 'وصول كامل لكل المحتوى التعليمي'
      });
      setLoading(false);
    } 
    // Check if this is bundle payment
    else if (id === 'bundle') {
      setIsBundle(true);
      loadBundleItems();
    } 
    else {
      loadCourse();
    }
  }, [id, user, authLoading]);

  const loadBundleItems = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const itemIds = params.get('items')?.split(',').map(Number) || [];
      
      if (itemIds.length === 0) {
        navigate(ROUTES.COURSES);
        return;
      }

      // Check for conflicts FIRST
      const conflicts = await checkForConflicts(itemIds);
      
      if (conflicts.hasConflict) {
        const messages = [];
        if (conflicts.alreadyOwned.length > 0) {
          messages.push(`لقد اشتريت بالفعل ${conflicts.alreadyOwned.length} من المحتويات في هذه الحزمة`);
        }
        if (conflicts.pendingConflicts.length > 0) {
          messages.push(`لديك ${conflicts.pendingConflicts.length} طلب دفع يدوي قيد المراجعة لمحتويات في هذه الحزمة`);
        }
        setConflictWarning(messages.join(' و '));
      }

      // Fetch all items
      const items = await Promise.all(
        itemIds.map(async (itemId) => {
          try {
            return await api.getCourse(itemId);
          } catch {
            try {
              return await api.getStorybook(itemId);
            } catch {
              return null;
            }
          }
        })
      );

      const validItems = items.filter(item => item !== null);
      setBundleItems(validItems);
      
      // Calculate bundle price with discount
      const totalPrice = validItems.reduce((sum, item) => sum + item.price, 0);
      
      // Apply bundle discount based on item count
      let discountPercent = 0;
      if (validItems.length === 2) discountPercent = 10;
      else if (validItems.length === 3) discountPercent = 15;
      else if (validItems.length === 4) discountPercent = 20;
      else if (validItems.length >= 5) discountPercent = 25;
      
      const discountAmount = Math.round(totalPrice * (discountPercent / 100));
      const finalPrice = totalPrice - discountAmount;
      
      setCourse({
        title_ar: `حزمة مخصصة (${validItems.length} محتويات)`,
        price: finalPrice,
        originalPrice: totalPrice,
        discount: discountPercent,
        savings: discountAmount,
        description_ar: validItems.map(item => item.title_ar).join(' + ')
      });
    } catch (error) {
      console.error('Error loading bundle items:', error);
      navigate(ROUTES.COURSES);
    } finally {
      setLoading(false);
    }
  };

  const loadCourse = async () => {
    try {
      // Try to load as course first
      try {
        const courseData = await api.getCourse(id);
        setCourse(courseData);
        
        // Check for conflicts for individual purchase
        const conflicts = await checkForConflicts([parseInt(id)]);
        if (conflicts.hasConflict) {
          if (conflicts.alreadyOwned.length > 0) {
            setConflictWarning('لقد اشتريت هذا المحتوى مسبقاً');
          } else if (conflicts.pendingConflicts.length > 0) {
            setConflictWarning('لديك طلب دفع يدوي قيد المراجعة لهذا المحتوى');
          }
        }
        return;
      } catch (err) {
        // If course not found, try storybook
        if (err.message && err.message.includes('الدورة غير موجودة')) {
          const storybookData = await api.getStorybook(id);
          setCourse(storybookData); // Using same state for both
          
          // Check for conflicts for individual purchase
          const conflicts = await checkForConflicts([parseInt(id)]);
          if (conflicts.hasConflict) {
            if (conflicts.alreadyOwned.length > 0) {
              setConflictWarning('لقد اشتريت هذا المحتوى مسبقاً');
            } else if (conflicts.pendingConflicts.length > 0) {
              setConflictWarning('لديك طلب دفع يدوي قيد المراجعة لهذا المحتوى');
            }
          }
          return;
        }
        throw err;
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let receiptBase64 = null;
      if (formData.receiptFile) {
        receiptBase64 = await fileToBase64(formData.receiptFile);
      }

      const requestData = {
        fullName: formData.fullName,
        phone: formData.phone,
        ccpSenderName: formData.ccpSenderName,
        receiptImage: receiptBase64
      };

      if (isSubscription) {
        requestData.purchaseType = 'subscription';
        requestData.courseId = null;
      } else if (isBundle) {
        requestData.purchaseType = 'bundle';
        requestData.courseIds = bundleItems.map(item => item.id);
        requestData.courseId = null;
      } else {
        requestData.courseId = parseInt(id);
      }

      await api.createManualPaymentRequest(requestData);

      // Track Facebook Pixel Purchase for manual payment request
      if (typeof window.fbq !== 'undefined') {
        const amount = course.price;
        const amountUSD = (amount / 250).toFixed(2); // Convert DZD to USD
        
        let contentIds = [];
        let contentName = 'Course Purchase - Manual Payment';
        let numItems = 1;
        
        if (isSubscription) {
          contentIds = ['subscription_monthly'];
          contentName = 'Monthly Subscription - Manual Payment';
        } else if (isBundle) {
          contentIds = bundleItems.map(item => item.id.toString());
          contentName = `Smart Bundle (${bundleItems.length} items) - Manual Payment`;
          numItems = bundleItems.length;
        } else {
          contentIds = [id];
        }
        
        window.fbq('track', 'Purchase', {
          value: parseFloat(amountUSD),
          currency: 'USD',
          content_type: isBundle ? 'bundle' : 'product',
          content_ids: contentIds,
          content_name: contentName,
          num_items: numItems
        });
        
        console.log('✅ Facebook Pixel: Manual payment purchase tracked', { 
          amount, 
          amountUSD, 
          contentName 
        });
      }

      showToast('تم إرسال طلب الدفع اليدوي بنجاح! سنراجعه خلال 24 ساعة.', 'success');
    } catch (error) {
      showToast(error.message || 'فشل إرسال الطلب', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast('حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت.', 'error');
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        showToast('نوع الملف غير مدعوم. يرجى رفع صورة (JPG, PNG) أو PDF.', 'error');
        return;
      }
      
      setFormData({ ...formData, receiptFile: file });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>الدورة غير موجودة</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-bg to-white py-20" dir="rtl">
      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-slideDown">
          <div className={`px-6 py-4 rounded-2xl shadow-2xl font-bold text-white flex items-center gap-3 min-w-[300px] max-w-md ${
            notification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
            notification.type === 'error' ? 'bg-gradient-to-r from-red-500 to-pink-600' :
            'bg-gradient-to-r from-blue-500 to-cyan-600'
          }`}>
            <i className={`fas ${
              notification.type === 'success' ? 'fa-check-circle' :
              notification.type === 'error' ? 'fa-exclamation-circle' :
              'fa-info-circle'
            } text-2xl`}></i>
            <span className="flex-1">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-custom-lg p-8 mb-6">
          <h1 className="text-3xl font-black text-text mb-2">
            طلب الدفع عبر CCP
          </h1>
          <p className="text-text2">
            يرجى ملء النموذج أدناه لطلب الدفع اليدوي عبر الحساب الجاري البريدي (CCP)
          </p>
        </div>

        {/* Course Info */}
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl p-6 mb-6">
          <h3 className="text-xl font-bold text-text mb-2">
            {isBundle ? 'الحزمة المطلوبة:' : 'الدورة المطلوبة:'}
          </h3>
          <p className="text-lg text-text2 mb-2">{course.title_ar}</p>
          
          {isBundle && course.originalPrice && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-text3 line-through">{course.originalPrice} دج</span>
                <span className="bg-green-500 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  خصم {course.discount}%
                </span>
              </div>
              <p className="text-2xl font-black text-primary">{course.price} دج</p>
              <p className="text-sm text-green-600 font-bold">
                <i className="fas fa-piggy-bank ml-1"></i>
                وفرت {course.savings} دج
              </p>
            </div>
          )}
          
          {!isBundle && (
            <p className="text-2xl font-black text-primary">{course.price} دج</p>
          )}
        </div>

        {/* Conflict Warning */}
        {conflictWarning && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-3xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-700 mb-2">
                  ⚠️ تنبيه مهم
                </h3>
                <p className="text-red-600 font-bold mb-3">
                  {conflictWarning}
                </p>
                <p className="text-sm text-red-500">
                  <i className="fas fa-info-circle ml-2"></i>
                  لا يمكن إتمام هذا الطلب حيث أن بعض المحتويات موجودة بالفعل أو قيد المراجعة. 
                  يرجى التحقق من لوحة التحكم الخاصة بك أو الانتظار حتى تتم مراجعة الطلبات السابقة.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Instructions */}
        <div className="bg-white rounded-3xl shadow-custom-lg p-8 mb-6">
          <h2 className="text-2xl font-black text-text mb-4 flex items-center">
            <i className="fas fa-money-bill-wave ml-3 text-primary"></i>
            معلومات الدفع
          </h2>

          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 space-y-4">
            <div>
              <p className="text-sm text-text3 mb-1">رقم الحساب الجاري البريدي (CCP):</p>
              <p className="text-2xl font-black text-text select-all">0025863012</p>
              <p className="text-sm text-text2 mt-1">المفتاح: 15</p>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm text-text3 mb-1">اسم صاحب الحساب:</p>
              <p className="text-lg font-bold text-text">Boufaa Moundir</p>
              <p className="text-sm text-text2 mt-1">الولاية: قسنطينة (Constantine)</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
              <p className="text-sm text-text2">
                <i className="fas fa-info-circle ml-2 text-yellow-600"></i>
                بعد إتمام التحويل، يرجى ملء النموذج أدناه. سنراجع طلبك خلال 24 ساعة.
              </p>
            </div>
          </div>
        </div>

        {/* Request Form */}
        <div className="bg-white rounded-3xl shadow-custom-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-text2 font-bold mb-2">الاسم الكامل *</label>
              <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none"
                placeholder="أدخل اسمك الكامل"
              />
            </div>

            <div>
              <label className="block text-text2 font-bold mb-2">رقم الهاتف *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none"
                placeholder="0xxxxxxxxx"
              />
            </div>

            <div>
              <label className="block text-text2 font-bold mb-2">اسم المحول (كما يظهر في الإيصال) *</label>
              <input
                type="text"
                required
                value={formData.ccpSenderName}
                onChange={(e) => setFormData({ ...formData, ccpSenderName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none"
                placeholder="الاسم الذي استخدمته في التحويل"
              />
            </div>

            <div>
              <label className="block text-text2 font-bold mb-2">
                صورة الإيصال / وصل التحويل *
                <span className="text-sm text-text3 font-normal mr-2">(JPG, PNG, PDF - حتى 10 ميجابايت)</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  onChange={handleFileChange}
                  required
                  className="hidden"
                  id="receipt-upload"
                />
                <label
                  htmlFor="receipt-upload"
                  className="w-full px-4 py-4 rounded-xl border-2 border-dashed border-primary bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer flex items-center justify-center gap-3"
                >
                  <i className="fas fa-cloud-upload-alt text-2xl text-primary"></i>
                  <div className="text-center">
                    {formData.receiptFile ? (
                      <div>
                        <p className="text-primary font-bold">{formData.receiptFile.name}</p>
                        <p className="text-xs text-text3">حجم: {(formData.receiptFile.size / 1024).toFixed(2)} كيلوبايت</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-primary font-bold">انقر لرفع صورة الإيصال</p>
                        <p className="text-xs text-text3">اختر ملف من جهازك</p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              {formData.receiptFile && (
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, receiptFile: null })}
                  className="mt-2 text-red-500 text-sm hover:text-red-700 flex items-center gap-1"
                >
                  <i className="fas fa-times-circle"></i>
                  إزالة الملف
                </button>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-text2">
                <i className="fas fa-shield-alt ml-2 text-blue-600"></i>
                بعد رفع الإيصال، سنراجع طلبك خلال 24 ساعة وسنفعل الدورة تلقائياً.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting || conflictWarning}
                className={`flex-1 px-6 py-4 rounded-xl font-bold transition-all duration-300 ${
                  conflictWarning 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-primary text-white hover:bg-primary-dark'
                } disabled:opacity-50`}
              >
                {conflictWarning 
                  ? 'لا يمكن إتمام الطلب'
                  : submitting 
                    ? 'جاري الإرسال...' 
                    : 'إرسال الطلب'}
              </button>
              <button
                type="button"
                onClick={() => navigate(isBundle ? '/bundles' : `/course/${id}`)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-4 rounded-xl font-bold hover:bg-gray-300 transition-all duration-300"
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManualPaymentRequestPage;
